import { defaultOperators } from 'react-querybuilder';
import type { Field } from 'react-querybuilder';

import { DataLake } from '@finley-technologies/finley-types';

import { fieldValidator } from './validate';

export type QueryBuilderFieldInputType =
  | 'string'
  | 'number'
  | 'date'
  | 'datetime'
  | 'boolean';

// =============================================================================
// ================================ Consts =====================================
// =============================================================================

const STRING_ONLY_OPS = [
  'contains',
  'beginsWith',
  'endsWith',
  'doesNotContain',
  'doesNotBeginWith',
  'doesNotEndWith',
];

// =============================================================================
// =========================== Types Conversions ===============================
// =============================================================================

export const athenaTablePropertyTypeToQueryBuilderType = (
  type: DataLake.AthenaTablePropertyTypeUnion,
): QueryBuilderFieldInputType => {
  switch (type) {
    case DataLake.AthenaTablePropertyType.Boolean:
      return 'boolean';
    case DataLake.AthenaTablePropertyType.TinyInt:
    case DataLake.AthenaTablePropertyType.SmallInt:
    case DataLake.AthenaTablePropertyType.Int:
    case DataLake.AthenaTablePropertyType.Integer:
    case DataLake.AthenaTablePropertyType.BigInt:
    case DataLake.AthenaTablePropertyType.Double:
    case DataLake.AthenaTablePropertyType.Float:
    case DataLake.AthenaTablePropertyType.Decimal:
      return 'number';
    case DataLake.AthenaTablePropertyType.Char:
    case DataLake.AthenaTablePropertyType.Varchar:
    case DataLake.AthenaTablePropertyType.String:
      return 'string';
    case DataLake.AthenaTablePropertyType.Date:
      return 'date';
    case DataLake.AthenaTablePropertyType.Timestamp:
      return 'datetime';
    default:
      return 'string';
  }
};

// =============================================================================
// ============================= Generate Configs ==============================
// =============================================================================

export const createDefaultConfig = ({
  name,
  label,
}: {
  name: string;
  label: string;
}): Field => {
  return {
    name,
    label,
    operators: defaultOperators,
    validator: fieldValidator,
  };
};

export const createBooleanConfig = ({
  name,
  label,
}: {
  name: string;
  label: string;
}): Field => {
  return {
    ...createDefaultConfig({ name, label }),
    operators: defaultOperators.filter((op) => op.name === '='),
    valueEditorType: 'select',
    values: [
      { name: 'TRUE', label: 'TRUE' },
      { name: 'FALSE', label: 'FALSE' },
    ],
    defaultValue: 'TRUE',
  };
};

export const createDateConfig = ({
  name,
  label,
}: {
  name: string;
  label: string;
}): Field => {
  return {
    ...createDefaultConfig({ name, label }),
    operators: defaultOperators.filter(
      (op) => !STRING_ONLY_OPS.includes(op.name),
    ),
    inputType: 'date',
  };
};

export const createDateTimeConfig = ({
  name,
  label,
}: {
  name: string;
  label: string;
}): Field => {
  return {
    ...createDefaultConfig({ name, label }),
    operators: defaultOperators.filter(
      (op) => !STRING_ONLY_OPS.includes(op.name),
    ),
    inputType: 'datetime-local',
  };
};

export const createNumericConfig = ({
  name,
  label,
}: {
  name: string;
  label: string;
}): Field => {
  return {
    ...createDefaultConfig({ name, label }),
    inputType: 'number',
  };
};

export const createStringConfig = ({
  name,
  label,
}: {
  name: string;
  label: string;
}): Field => {
  return {
    ...createDefaultConfig({ name, label }),
  };
};

export const generateFieldConfig = ({
  name,
  label,
  fieldType,
}: {
  name: string;
  label: string;
  fieldType: QueryBuilderFieldInputType;
}): Field => {
  switch (fieldType) {
    case 'boolean':
      return createBooleanConfig({ name, label });
    case 'date':
      return createDateConfig({ name, label });
    case 'number':
      return createNumericConfig({ name, label });
    case 'string':
      return createStringConfig({ name, label });
    default:
      return createDefaultConfig({ name, label });
  }
};
